import { useCallback, useEffect, useRef, useState, type FC } from 'react'

import { connectPuzzelApi, getPuzzelApi, resetPuzzelApi } from './puzzelApi'

type SystemCall = {
  system_call_progress:
    | 'CONNECTED'
    | 'HANGUP'
    | 'ALERTING'
    | 'NOANSWER'
    | 'ABORTED'
    | 'SPLIT'
    | 'BUSY'
  system_caller_ano: string
  language: string
  callerStatus: 'New Call'
  current_state: string
  system_caller_transfered: boolean
  __CLIENT_IS_OUTBOUND__: boolean
  ciq_destination: string
  system_session_id: string
}

type PuzzelCall = {
  sessionId: string
  caller: string
}

export const PuzzelEvents: FC = () => {
  const [data, setData] = useState<{ event: string; data: unknown }[]>([])
  const [logs, setLogs] = useState<string[]>([])
  const [connectionStatus, setConnectionStatus] = useState<
    'idle' | 'connecting' | 'connected' | 'error'
  >('idle')
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [isRunningTestCalls, setIsRunningTestCalls] = useState<boolean>(false)
  const [autoFollow, setAutoFollow] = useState<boolean>(true)
  const [showFilters, setShowFilters] = useState({
    postMessages: true,
    pccEvents: true,
    debug: true,
  })
  const logsEndRef = useRef<HTMLDivElement>(null)

  const filterLogs = useCallback(
    (logLines: string[]) => {
      return logLines.filter((line) => {
        if (line.includes('WINDOW_MESSAGE')) return showFilters.postMessages
        if (line.includes('EVENT_')) return showFilters.pccEvents
        return showFilters.debug
      })
    },
    [showFilters],
  )
  const statusColor =
    connectionStatus === 'connected'
      ? '#1a7f37'
      : connectionStatus === 'connecting'
        ? '#bf8700'
        : connectionStatus === 'error'
          ? '#d1242f'
          : '#6e7781'

  // oxlint-disable-next-line typescript/no-explicit-any -- no types here
  const apiRef = useRef<any>(null)
  const unsubsRef = useRef<(() => void)[]>([])
  const connectAttemptRef = useRef<number>(0)
  const messageStatsRef = useRef<{
    total: number
    fromPuzzel: number
    ports0: number
    ports1plus: number
  }>({ total: 0, fromPuzzel: 0, ports0: 0, ports1plus: 0 })
  const [messageStats, setMessageStats] = useState(messageStatsRef.current)

  const log = useCallback((message: string, details?: unknown) => {
    const ts = new Date().toISOString()
    const line =
      details === undefined
        ? `${ts} ${message}`
        : `${ts} ${message} ${safeJson(details)}`

    // oxlint-disable-next-line no-console -- debug widget
    console.log('[events-widget]', message, details)

    setLogs((prev) => {
      const next = [...prev, line]
      return next.length > 200 ? next.slice(-200) : next
    })
  }, [])

  const cleanupSubscriptions = useCallback(() => {
    unsubsRef.current.forEach((unsub) => {
      try {
        unsub()
      } catch {
        // ignore
      }
    })
    unsubsRef.current = []
  }, [])

  const connectNow = useCallback(
    async (reason: string): Promise<void> => {
      connectAttemptRef.current += 1
      const attemptId = connectAttemptRef.current

      setConnectionStatus('connecting')
      setConnectionError(null)
      setData([])

      cleanupSubscriptions()

      // Only reset on explicit reconnect, not on auto-connect
      // This prevents race conditions with React StrictMode double-mount
      const shouldReset = reason.includes('reconnect')
      if (shouldReset) {
        resetPuzzelApi()
      }

      log('CONNECT_START', {
        reason,
        attemptId,
        documentReadyState: document.readyState,
        href: window.location.href,
      })

      try {
        if (document.readyState === 'loading') {
          log('WAIT_FOR_DOM_READY')
        }
        // Use getPuzzelApi() for caching on auto-connect,
        // use connectPuzzelApi() directly only on explicit reconnect
        const api = shouldReset
          ? await connectPuzzelApi({
              onRetry: (info) => {
                log('CONNECT_RETRY', info)
              },
            })
          : await getPuzzelApi()
        if (attemptId !== connectAttemptRef.current) return

        apiRef.current = api
        setConnectionStatus('connected')
        log('CONNECT_SUCCESS', {
          attemptId,
          // oxlint-disable-next-line typescript/no-explicit-any -- debug widget
          config: (api as any)?._config,
        })

        // Subscribe to events early so we don't miss anything.
        // oxlint-disable-next-line typescript/no-explicit-any -- debug widget
        const a: any = api
        unsubsRef.current.push(
          a.on('SYSTEM_REQUEST_IS_CONNECTED', (payload: unknown) => {
            log('EVENT_SYSTEM_REQUEST_IS_CONNECTED', payload)
          }),
        )
        unsubsRef.current.push(
          a.on('SYSTEM_INCOMING_CALL', (payload: { call: PuzzelCall }) => {
            setData((d) => [
              ...d,
              { event: 'SYSTEM_INCOMING_CALL', data: payload },
            ])
            log('EVENT_SYSTEM_INCOMING_CALL', payload)
          }),
        )
        unsubsRef.current.push(
          a.on('SYSTEM_CALLOUT_CALL', (payload: unknown) => {
            setData((d) => [
              ...d,
              { event: 'SYSTEM_CALLOUT_CALL', data: payload },
            ])
            log('EVENT_SYSTEM_CALLOUT_CALL', payload)
          }),
        )
        unsubsRef.current.push(
          a.on('SYSTEM_CALL_STATE_CHANGE', (payload: unknown) => {
            setData((d) => [
              ...d,
              { event: 'SYSTEM_CALL_STATE_CHANGE', data: payload },
            ])
            log('EVENT_SYSTEM_CALL_STATE_CHANGE', payload)
          }),
        )
        unsubsRef.current.push(
          a.on('AGENT_TO_AGENT_CALL', (payload: unknown) => {
            setData((d) => [
              ...d,
              { event: 'AGENT_TO_AGENT_CALL', data: payload },
            ])
            log('EVENT_AGENT_TO_AGENT_CALL', payload)
          }),
        )
        unsubsRef.current.push(
          a.on('SYSTEM_CALL_TRIGGER', (vars: SystemCall) => {
            setData((d) => [...d, { event: 'SYSTEM_CALL_TRIGGER', data: vars }])
            log('EVENT_SYSTEM_CALL_TRIGGER', vars)
          }),
        )
        unsubsRef.current.push(
          a.on('SYSTEM_CALL_ASR_TRANSCRIPTION_TRIGGER', (payload: unknown) => {
            setData((d) => [
              ...d,
              { event: 'SYSTEM_CALL_ASR_TRANSCRIPTION_TRIGGER', data: payload },
            ])
            log('EVENT_SYSTEM_CALL_ASR_TRANSCRIPTION_TRIGGER', payload)
          }),
        )
        unsubsRef.current.push(
          a.on('SYSTEM_CALL_ASR_TRANSCRIPTION', (payload: unknown) => {
            setData((d) => [
              ...d,
              { event: 'SYSTEM_CALL_ASR_TRANSCRIPTION', data: payload },
            ])
            log('EVENT_SYSTEM_CALL_ASR_TRANSCRIPTION', payload)
          }),
        )

        log('SUBSCRIBED', { count: unsubsRef.current.length })

        await a.ready()
        log('READY_SENT')

        // Expose the API for manual inspection in DevTools:
        const debugWindow = window as unknown as {
          __eventsWidgetApi?: unknown
        }
        debugWindow.__eventsWidgetApi = api
      } catch (error) {
        if (attemptId !== connectAttemptRef.current) return
        const message = error instanceof Error ? error.message : String(error)
        setConnectionStatus('error')
        setConnectionError(message)
        log('CONNECT_FAILED', { attemptId, message })
      }
    },
    [cleanupSubscriptions, log],
  )

  const runTestCalls = useCallback(async (): Promise<void> => {
    const api = apiRef.current
    const attemptId = connectAttemptRef.current

    if (!api) {
      log('TEST_CALLS_SKIPPED_NO_API')
      return
    }

    setIsRunningTestCalls(true)
    log('TEST_CALLS_START', { attemptId })
    try {
      log('CALL_widget.uid_START')
      try {
        // oxlint-disable-next-line typescript/no-explicit-any -- debug widget
        const widgetUid = await (api as any).call('widget.uid')
        if (attemptId !== connectAttemptRef.current || apiRef.current !== api) {
          log('TEST_CALLS_ABORTED_STALE_CONNECTION', { attemptId })
          return
        }
        log('CALL_widget.uid_OK', { widgetUid })
      } catch (err) {
        if (attemptId !== connectAttemptRef.current || apiRef.current !== api) {
          log('TEST_CALLS_ABORTED_STALE_CONNECTION', { attemptId })
          return
        }
        log('CALL_widget.uid_FAILED', {
          message: err instanceof Error ? err.message : String(err),
        })
      }

      log('CALL_call.getCurrentCall_START')
      try {
        // oxlint-disable-next-line typescript/no-explicit-any -- debug widget
        const currentCall = await (api as any).call('call.getCurrentCall')
        if (attemptId !== connectAttemptRef.current || apiRef.current !== api) {
          log('TEST_CALLS_ABORTED_STALE_CONNECTION', { attemptId })
          return
        }
        log('CALL_call.getCurrentCall_OK', { currentCall })
      } catch (err) {
        if (attemptId !== connectAttemptRef.current || apiRef.current !== api) {
          log('TEST_CALLS_ABORTED_STALE_CONNECTION', { attemptId })
          return
        }
        log('CALL_call.getCurrentCall_FAILED', {
          message: err instanceof Error ? err.message : String(err),
        })
      }

      log('TEST_CALLS_DONE', { attemptId })
    } finally {
      setIsRunningTestCalls(false)
    }
  }, [log])

  useEffect(() => {
    log('MOUNT', {
      documentReadyState: document.readyState,
      href: window.location.href,
    })

    const onMessage = (e: MessageEvent): void => {
      messageStatsRef.current.total += 1
      const ports = e.ports?.length ?? 0
      const isPuzzel = e.origin.includes('puzzel.com')

      if (isPuzzel) {
        messageStatsRef.current.fromPuzzel += 1
        if (ports > 0) messageStatsRef.current.ports1plus += 1
        if (ports === 0) messageStatsRef.current.ports0 += 1
      }

      // Log ALL messages
      log('WINDOW_MESSAGE', {
        origin: e.origin,
        ports,
        data: e.data,
      })

      setMessageStats({ ...messageStatsRef.current })
    }

    window.addEventListener('message', onMessage, true)

    // Auto-connect once on page load.
    connectNow('auto')

    return () => {
      log('UNMOUNT')
      window.removeEventListener('message', onMessage, true)
      cleanupSubscriptions()
    }
  }, [cleanupSubscriptions, connectNow, log])

  // oxlint-disable-next-line react/exhaustive-deps -- we want to scroll to latest logs
  useEffect(() => {
    if (autoFollow && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [logs, autoFollow])

  return (
    <div style={{ fontFamily: 'monospace', padding: 12 }}>
      <h2>[events-widget] PCC Widget API Debug</h2>

      <div>
        <strong>Status:</strong>{' '}
        <span style={{ color: statusColor, fontWeight: 700 }}>
          {connectionStatus}
        </span>
        {connectionError ? ` (${connectionError})` : ''}
      </div>
      <div>
        <strong>document.readyState:</strong> {document.readyState}
      </div>
      <div>
        <strong>Window messages (postMessage):</strong>
        <div style={{ marginLeft: 12 }}>
          <div>Total: {messageStats.total}</div>
          <div>From puzzel.com origin: {messageStats.fromPuzzel}</div>
          <div>With ports.length === 0: {messageStats.ports0}</div>
          <div>With ports.length &gt; 0: {messageStats.ports1plus}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 8 }}>
        <button
          type="button"
          onClick={() => {
            connectNow('manual_reconnect')
          }}
          disabled={connectionStatus === 'connecting'}
        >
          Reconnect
        </button>
        <button
          type="button"
          onClick={() => {
            runTestCalls()
          }}
          disabled={connectionStatus !== 'connected' || isRunningTestCalls}
        >
          Run test calls
        </button>
        <button
          type="button"
          onClick={() => {
            setLogs([])
            setData([])
            messageStatsRef.current = {
              total: 0,
              fromPuzzel: 0,
              ports0: 0,
              ports1plus: 0,
            }
            setMessageStats(messageStatsRef.current)
            log('CLEARED')
          }}
        >
          Clear
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 8,
          flexWrap: 'wrap',
        }}
      >
        <h3 style={{ margin: 0 }}>
          Logs ({filterLogs(logs).length}/{logs.length})
        </h3>
        <span style={{ color: '#666' }}>|</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <input
            type="checkbox"
            checked={showFilters.postMessages}
            onChange={(e) =>
              setShowFilters((f) => ({ ...f, postMessages: e.target.checked }))
            }
          />
          postMessages
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <input
            type="checkbox"
            checked={showFilters.pccEvents}
            onChange={(e) =>
              setShowFilters((f) => ({ ...f, pccEvents: e.target.checked }))
            }
          />
          PCC events
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <input
            type="checkbox"
            checked={showFilters.debug}
            onChange={(e) =>
              setShowFilters((f) => ({ ...f, debug: e.target.checked }))
            }
          />
          debug
        </label>
        <span style={{ color: '#666' }}>|</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <input
            type="checkbox"
            checked={autoFollow}
            onChange={(e) => setAutoFollow(e.target.checked)}
          />
          follow
        </label>
      </div>
      <pre
        style={{
          maxHeight: 260,
          overflow: 'auto',
          background: '#0b1020',
          color: '#d9e1ff',
          padding: 8,
        }}
      >
        {filterLogs(logs).join('\n')}
        <div ref={logsEndRef} />
      </pre>

      <h3>[events-widget] Events ({data.length})</h3>
      <pre style={{ maxHeight: 420, overflow: 'auto' }}>
        {JSON.stringify(data, undefined, '  ')}
      </pre>
    </div>
  )
}

const safeJson = (value: unknown): string => {
  try {
    return JSON.stringify(value)
  } catch {
    return '[unserializable]'
  }
}
